import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Megaphone, ImagePlus, Check, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
const STATUS = {
  pending_setup: { label: "Complete your setup", cls: "bg-blaze/15 text-blaze" },
  pending_review: { label: "Pending review", cls: "bg-[#2f6690]/15 text-[#2f6690]" },
  active: { label: "Active", cls: "bg-[#2f7d4f]/15 text-[#2f7d4f]" },
  needs_changes: { label: "Needs changes", cls: "bg-blaze/15 text-blaze" },
  paused: { label: "Paused", cls: "bg-saddle/15 text-saddle" },
  cancelled: { label: "Cancelled", cls: "bg-charcoal/10 text-charcoal/60" },
  expired: { label: "Expired", cls: "bg-charcoal/10 text-charcoal/60" },
  rejected: { label: "Rejected", cls: "bg-destructive/15 text-destructive" },
};

function ImageField({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const res = await api.adUpload(file); onChange(res.url); toast.success(`${label} uploaded`); }
    catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <label className="hr-overline text-saddle">{label}</label>
      <div className="flex items-center gap-3 mt-1">
        <div className="w-16 h-16 bg-bone border border-saddle/20 overflow-hidden grid place-items-center shrink-0">
          {value ? <img src={mediaSrc(value)} alt={label} className="w-full h-full object-cover" /> : <ImagePlus size={18} className="text-saddle/40" />}
        </div>
        <label className="inline-flex items-center gap-1.5 border border-forest/40 text-forest px-3 py-2 text-sm font-semibold cursor-pointer hover:bg-forest hover:text-bone transition-colors" data-testid={testid}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {value ? "Replace" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>
    </div>
  );
}

function SetupForm({ ad, onDone }) {
  const s = ad.setup || {};
  const [form, setForm] = useState({
    business_name: s.business_name || ad.business_name || "", contact_name: s.contact_name || "", email: s.email || "",
    phone: s.phone || "", website: s.website || "", instagram: s.instagram || "", category: s.category || "",
    states_served: s.states_served || "", description: s.description || "", logo: s.logo || "", creative: s.creative || "",
    feature_topic: s.feature_topic || "", promo_copy: s.promo_copy || "", cta_link: s.cta_link || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const placement = ad.placement_type;
  const wantsCreative = ["banner", "homepage", "state", "bundle"].includes(placement);
  const wantsFeature = placement === "feature";
  const wantsNewsletter = placement === "newsletter";

  const submit = async () => {
    if (!form.business_name || !form.email) return toast.error("Business name and email are required");
    setBusy(true);
    try { await api.adSetup(ad.id, form); toast.success("Submitted for review — we'll approve your placement shortly."); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const I = (k, ph, type = "text") => (
    <input value={form[k]} onChange={set(k)} placeholder={ph} type={type} className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none focus:border-forest" data-testid={`setup-${k}`} />
  );

  return (
    <div className="mt-4 border-t border-saddle/15 pt-4 space-y-3" data-testid="setup-form">
      <div className="grid sm:grid-cols-2 gap-3">
        {I("business_name", "Business name *")}
        {I("contact_name", "Contact name")}
        {I("email", "Email *", "email")}
        {I("phone", "Phone")}
        {I("website", "Website")}
        {I("instagram", "Instagram / social")}
        {I("category", "Business category")}
        {I("states_served", "State(s) served")}
      </div>
      <textarea value={form.description} onChange={set("description")} rows={3} placeholder="Business description" className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none focus:border-forest" data-testid="setup-description" />
      <div className="grid sm:grid-cols-2 gap-3">
        <ImageField label="Logo" value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} testid="setup-logo-upload" />
        {wantsCreative && <ImageField label="Advertisement / banner artwork" value={form.creative} onChange={(v) => setForm({ ...form, creative: v })} testid="setup-creative-upload" />}
        {wantsFeature && <ImageField label="Feature photo" value={form.creative} onChange={(v) => setForm({ ...form, creative: v })} testid="setup-feature-photo" />}
      </div>
      {wantsFeature && I("feature_topic", "Feature topic")}
      {(wantsNewsletter || wantsFeature || wantsCreative) && (
        <>
          <textarea value={form.promo_copy} onChange={set("promo_copy")} rows={2} placeholder="Promotional copy / message" className="w-full border border-saddle/25 px-3 py-2 text-sm outline-none focus:border-forest" data-testid="setup-promo" />
          {I("cta_link", "CTA link (where the ad points)")}
        </>
      )}
      <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 bg-forest text-bone px-6 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="setup-submit">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Submit for review
      </button>
    </div>
  );
}

function AdRow({ ad, onDone }) {
  const st = STATUS[ad.status] || STATUS.pending_setup;
  const needsSetup = ["pending_setup", "needs_changes"].includes(ad.status);
  const [open, setOpen] = useState(needsSetup);
  return (
    <div className="bg-white border border-saddle/20 p-5" data-testid="advertiser-ad-row">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-lg text-forest">{ad.product_name}{ad.state ? ` · ${ad.state}` : ""}</h3>
          <p className="text-xs text-charcoal/50 mt-0.5">{dollars(ad.amount_cents)}{ad.billing_cycle === "monthly" ? "/mo" : ad.billing_cycle === "annual" ? "/yr" : " one-time"}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-bold uppercase px-2 py-1 ${st.cls}`} data-testid={`ad-status-${ad.id}`}>{st.label}</span>
          {!needsSetup && ad.status !== "cancelled" && (
            <button onClick={() => setOpen(!open)} className="text-saddle hover:text-forest" aria-label="Edit" data-testid="advertiser-edit">
              {open ? <X size={16} /> : <Pencil size={16} />}
            </button>
          )}
        </div>
      </div>
      {ad.admin_note && ad.status === "needs_changes" && <p className="text-sm text-blaze mt-2" data-testid="ad-admin-note">Note from HomeRange: {ad.admin_note}</p>}
      {open && <SetupForm ad={ad} onDone={onDone} />}
    </div>
  );
}

export default function AdvertiserDashboard() {
  const navigate = useNavigate();
  const { isAuthed, ready } = useAuth();
  const { openAuth } = useUI();
  const [ads, setAds] = useState(null);

  const load = useCallback(() => { api.myAdvertising().then((d) => setAds(d.advertisements)).catch(() => setAds([])); }, []);
  useEffect(() => { if (isAuthed) load(); }, [isAuthed, load]);

  if (!ready) {
    return <div className="min-h-screen bg-paper grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>;
  }

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-paper grid place-items-center px-6">
        <div className="text-center">
          <Megaphone size={44} className="mx-auto text-forest" />
          <h1 className="font-serif text-3xl text-forest mt-4">Your advertising</h1>
          <p className="text-charcoal/60 mt-2">Sign in to manage your HomeRange advertising.</p>
          <button onClick={() => openAuth("login")} className="mt-5 bg-forest text-bone px-6 py-3 font-semibold" data-testid="advertiser-signin">Sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="advertiser-dashboard-page">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/advertise")} className="inline-flex items-center gap-1.5 text-sm text-saddle hover:text-forest mb-6" data-testid="advertiser-back"><ArrowLeft size={15} /> Advertising options</button>
        <span className="hr-overline text-saddle block">Welcome to HomeRange Advertising</span>
        <h1 className="font-serif text-4xl text-forest mt-2">Your advertising dashboard</h1>
        <p className="text-charcoal/60 mt-2 text-sm">Complete your advertising profile so we can activate each placement. Every advertisement is reviewed before it goes live.</p>

        <div className="mt-8 space-y-3">
          {ads === null ? (
            <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
          ) : ads.length === 0 ? (
            <div className="bg-white border border-saddle/20 p-10 text-center" data-testid="advertiser-empty">
              <p className="text-charcoal/60">You don't have any advertising yet.</p>
              <button onClick={() => navigate("/advertise")} className="mt-4 bg-blaze text-white px-6 py-3 font-semibold hover:bg-blaze-dark transition-colors" data-testid="advertiser-browse">Browse advertising options</button>
            </div>
          ) : (
            ads.map((ad) => <AdRow key={ad.id} ad={ad} onDone={load} />)
          )}
        </div>
      </div>
    </div>
  );
}
